import React, { useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Send, History, Settings } from "lucide-react";

export default function GrokAI() {
  const [prompt, setPrompt] = useState("");
  const [systemMessage, setSystemMessage] = useState("You are a helpful AI assistant.");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Advanced settings
  const [maxTokens, setMaxTokens] = useState(1024);
  const [temperature, setTemperature] = useState(0.7);
  const [store, setStore] = useState(false);
  
  const [status, setStatus] = useState(null);

  React.useEffect(() => {
    checkStatus();
    loadHistory();
  }, []);

  const checkStatus = async () => {
    try {
      const res = await api.get("/api/grok/status");
      setStatus(res.data);
      if (!res.data.configured) {
        toast.error("Grok AI not configured. Add XAI_API_KEY to backend/.env");
      }
    } catch (e) {
      toast.error("Failed to check Grok AI status");
    }
  };

  const loadHistory = async () => {
    try {
      const res = await api.get("/api/grok/history?limit=10");
      setHistory(res.data.generations || []);
    } catch (e) {
      console.error("Failed to load history:", e);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setLoading(true);
    setResponse("");
    
    try {
      const res = await api.post("/api/grok/generate", {
        prompt: prompt.trim(),
        system_message: systemMessage,
        max_tokens: maxTokens,
        temperature,
        store,
      });
      
      setResponse(res.data.text);
      toast.success(`Generated ${res.data.tokens_used || 0} tokens`);
      loadHistory();
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to generate text";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout
      title="Grok AI"
      overline="AI TOOLS"
      actions={
        <>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="btn-ghost inline-flex items-center gap-2"
          >
            <History size={16} />
            History
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="btn-ghost inline-flex items-center gap-2"
          >
            <Settings size={16} />
            Settings
          </button>
        </>
      }
    >
      <div className="max-w-4xl space-y-6">
        {/* Status */}
        {status && !status.configured && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
            <p className="text-sm text-red-300">
              ⚠️ Grok AI is not configured. Add <code className="bg-black/30 px-2 py-1 rounded">XAI_API_KEY</code> to backend/.env
            </p>
          </div>
        )}

        {status && status.configured && (
          <div className="rounded-lg border p-4" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={16} style={{ color: "var(--gio-primary)" }} />
              <span className="text-sm font-bold" style={{ color: "var(--gio-text)" }}>Status: Active</span>
            </div>
            <p className="text-xs" style={{ color: "var(--gio-muted-2)" }}>
              Model: {status.model} • Base URL: {status.base_url}
            </p>
          </div>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className="rounded-lg border p-4 space-y-4" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
            <h3 className="font-bold text-sm" style={{ color: "var(--gio-text)" }}>Advanced Settings</h3>
            
            <div>
              <label className="block text-xs mb-2" style={{ color: "var(--gio-muted-2)" }}>System Message</label>
              <textarea
                value={systemMessage}
                onChange={(e) => setSystemMessage(e.target.value)}
                className="w-full rounded border px-3 py-2 text-sm"
                style={{ borderColor: "var(--gio-border)", background: "var(--gio-bg)", color: "var(--gio-text)" }}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs mb-2" style={{ color: "var(--gio-muted-2)" }}>Max Tokens: {maxTokens}</label>
                <input
                  type="range"
                  min="256"
                  max="4096"
                  step="256"
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(Number(e.target.value))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs mb-2" style={{ color: "var(--gio-muted-2)" }}>Temperature: {temperature}</label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => setTemperature(Number(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="store"
                checked={store}
                onChange={(e) => setStore(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="store" className="text-xs" style={{ color: "var(--gio-muted-2)" }}>
                Allow xAI to store response for 30 days
              </label>
            </div>
          </div>
        )}

        {/* Main Form */}
        <form onSubmit={handleGenerate} className="space-y-4">
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
              Prompt
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ask Grok anything..."
              className="w-full rounded border px-4 py-3 text-sm"
              style={{ borderColor: "var(--gio-border)", background: "var(--gio-bg)", color: "var(--gio-text)" }}
              rows={6}
              maxLength={8000}
              required
            />
            <p className="text-xs mt-1" style={{ color: "var(--gio-muted-3)" }}>
              {prompt.length} / 8000 characters
            </p>
          </div>

          <button
            type="submit"
            disabled={loading || !prompt.trim() || (status && !status.configured)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Send size={16} />
            {loading ? "Generating..." : "Generate with Grok"}
          </button>
        </form>

        {/* Response */}
        {response && (
          <div className="rounded-lg border p-6 space-y-3" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
            <div className="flex items-center gap-2">
              <Sparkles size={16} style={{ color: "var(--gio-primary)" }} />
              <h3 className="font-bold text-sm" style={{ color: "var(--gio-text)" }}>Response</h3>
            </div>
            <div className="whitespace-pre-wrap text-sm leading-relaxed" style={{ color: "var(--gio-text)" }}>
              {response}
            </div>
          </div>
        )}

        {/* History */}
        {showHistory && history.length > 0 && (
          <div className="rounded-lg border p-4 space-y-3" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
            <h3 className="font-bold text-sm flex items-center gap-2" style={{ color: "var(--gio-text)" }}>
              <History size={16} />
              Recent Generations
            </h3>
            <div className="space-y-3">
              {history.map((item, idx) => (
                <div
                  key={idx}
                  className="rounded border p-3 space-y-2"
                  style={{ borderColor: "var(--gio-border)", background: "var(--gio-bg)" }}
                >
                  <p className="text-xs font-bold" style={{ color: "var(--gio-muted-2)" }}>
                    {new Date(item.created_at).toLocaleString()} • {item.tokens_used || 0} tokens
                  </p>
                  <p className="text-sm" style={{ color: "var(--gio-text)" }}>
                    <strong>Prompt:</strong> {item.prompt.slice(0, 100)}{item.prompt.length > 100 ? "..." : ""}
                  </p>
                  <p className="text-sm" style={{ color: "var(--gio-muted-2)" }}>
                    <strong>Answer:</strong> {item.answer.slice(0, 200)}{item.answer.length > 200 ? "..." : ""}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
